import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '../lib/queryClient';
import superjson from 'superjson';
import {
  FolderOpen,
  Upload,
  Trash2,
  Share2,
  Copy,
  Lock,
  Unlock,
  Image as ImageIcon,
  FileText,
  File,
  X,
  ExternalLink,
  Loader2,
  Check,
  Github,
  Folder,
  Pencil,
  ChevronRight,
  ArrowLeft,
  Download,
  Plus,
  Eye,
  MessageSquarePlus,
  Sparkles,
  Music,
  Video,
  FileAudio,
  Box,
  LayoutGrid,
  Grid3X3,
  List,
  GalleryHorizontalEnd,
  FolderPlus,
  FolderInput,
  Star,
  Link2,
  Calendar,
  Tag,
  Eraser,
  WandSparkles,
  SendHorizontal,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './Button';
import { Input } from './Input';
import { Tooltip, TooltipContent, TooltipTrigger } from './Tooltip';
import {
  getChatAttachmentsList,
  ChatAttachmentItem,
} from '../endpoints/chat-attachments/list_GET.schema';
import { uploadChatAttachment } from '../endpoints/chat-attachments/upload_POST.schema';
import { uploadChatAttachmentSmart } from '../endpoints/chat-attachments/upload-direct_POST.schema';
import { shareChatAttachment } from '../endpoints/chat-attachments/share_POST.schema';
import { deleteChatAttachment } from '../endpoints/chat-attachments/delete_POST.schema';
import { useUserIntegrations, useConnectGitHub } from '../helpers/useUserSettings';
import { useGithubRepos, useGithubBranches } from '../helpers/useGithubApi';
import { getGithubContents } from '../endpoints/github/contents_GET.schema';
import { getGithubFile } from '../endpoints/github/file_GET.schema';
import { ShareAssetDialog } from './ShareAssetDialog';
import { GlbViewer, isGlbFile } from './GlbViewer';
import {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSub,
  ContextMenuSubTrigger,
  ContextMenuSubContent,
  ContextMenuSeparator,
} from './ContextMenu';
import styles from './ChatAttachmentsLibrary.module.css';

interface ChatAttachmentsLibraryProps {
  sessionId: string;
  isOpen: boolean;
  onClose: () => void;
  onSelectAttachment?: (attachment: ChatAttachmentItem) => void;
  variant?: 'modal' | 'inline';
  onImageEnhance?: (imageUrl: string) => void;
  onRemoveBackground?: (imageUrl: string) => void;
  onSendToChat?: (attachment: ChatAttachmentItem) => void;
}

type TabType = 'attachments' | 'generated' | 'github';

interface UserMediaItem {
  id: string;
  userId: number;
  fileName: string;
  fileType: string | null;
  fileSize: number | null;
  url: string;
  thumbnailUrl: string | null;
  source: string;
  prompt: string | null;
  provider: string | null;
  r2Key: string | null;
  metadata: string | null;
  createdAt: string | null;
  folderId?: string | null;
}

type AssetTypeFilter = 'all' | 'image' | 'video' | 'audio' | '3d' | 'document' | 'other';
type ViewMode = 'small-thumbs' | 'large-thumbs' | 'tiled' | 'details';

interface AssetFilterOption {
  value: AssetTypeFilter;
  label: string;
  icon: React.ReactNode;
}

const MEDIA_PAGE_SIZE = 50;

async function fetchUserMedia(offset = 0): Promise<{ items: UserMediaItem[]; total: number; hasMore: boolean; error?: boolean }> {
  const res = await fetch(`/_api/user-media/list?limit=${MEDIA_PAGE_SIZE}&offset=${offset}`);
  if (!res.ok) {
    console.warn('[fetchUserMedia] HTTP', res.status, 'for offset', offset);
    return { items: [], total: 0, hasMore: false, error: true };
  }
  const text = await res.text();
  try {
    return superjson.parse(text);
  } catch {
    try { return JSON.parse(text); } catch { return { items: [], total: 0, hasMore: false }; }
  }
}

async function deleteUserMedia(id: string): Promise<{ success: boolean }> {
  const res = await fetch('/_api/user-media/delete', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: superjson.stringify({ id }),
  });
  const text = await res.text();
  try {
    return superjson.parse(text);
  } catch {
    return JSON.parse(text);
  }
}

function buildPdfLightboxSrcDoc(_pdfUrl: string): string {
  return `<!doctype html><html lang="en"><head><meta charset="utf-8"/><meta name="viewport" content="width=device-width,initial-scale=1"/>
<script src="https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.9.155/pdf.min.mjs" type="module"><\/script>
<style>
*{margin:0;padding:0;box-sizing:border-box}
html,body{width:100%;height:100%;background:#111;font-family:Inter,system-ui,sans-serif;color:#fff;overflow:hidden}
#viewer{display:flex;flex-direction:column;height:100%}
#canvasWrap{flex:1;display:flex;align-items:center;justify-content:center;overflow:auto;position:relative;gap:2px}
canvas{display:block;background:#fff;box-shadow:0 2px 8px rgba(0,0,0,.4)}
#controls{display:flex;align-items:center;justify-content:center;padding:8px 12px;background:#1a1a1a;border-top:1px solid #2a2a2a;gap:8px;flex-shrink:0;flex-wrap:wrap}
#controls button{background:#222;border:1px solid #333;color:#ccc;padding:5px 14px;border-radius:5px;cursor:pointer;font-size:12px;font-weight:600;display:flex;align-items:center;gap:3px}
#controls button:hover{background:#2a2a2a;border-color:#444}
#controls button:disabled{opacity:.3;cursor:default}
#controls button.active{background:#f97316;border-color:#f97316;color:#fff}
.sep{width:1px;height:20px;background:#333;flex-shrink:0}
#pageInfo{font-size:12px;color:#888;white-space:nowrap}
#zoomInfo{font-size:11px;color:#666;white-space:nowrap}
#loading{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;color:#666;font-size:14px}
</style></head><body><div id="viewer">
<div id="canvasWrap"><div id="loading">Loading PDF...</div><canvas id="c1"></canvas><canvas id="c2" style="display:none"></canvas></div>
<div id="controls">
<button id="prev" disabled>&#9664;</button>
<span id="pageInfo">—</span>
<button id="next" disabled>&#9654;</button>
<div class="sep"></div>
<button id="zoomOut">−</button><span id="zoomInfo">Fit</span><button id="zoomIn">+</button><button id="zoomFit">Fit</button>
<div class="sep"></div>
<button id="spreadBtn">&#9776; Spread</button>
</div></div>
<script type="module">
import{GlobalWorkerOptions,getDocument}from"https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.9.155/pdf.min.mjs";
GlobalWorkerOptions.workerSrc="https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.9.155/pdf.worker.min.mjs";
const c1=document.getElementById("c1"),c2=document.getElementById("c2");
const ctx1=c1.getContext("2d"),ctx2=c2.getContext("2d");
const info=document.getElementById("pageInfo"),zoomInfo=document.getElementById("zoomInfo");
const prevBtn=document.getElementById("prev"),nextBtn=document.getElementById("next");
const zoomInBtn=document.getElementById("zoomIn"),zoomOutBtn=document.getElementById("zoomOut"),zoomFitBtn=document.getElementById("zoomFit");
const spreadBtn=document.getElementById("spreadBtn"),loadEl=document.getElementById("loading");
let pdf=null,pg=1,rendering=false,userZoom=0,spread=false;
const ZS=[0.5,0.75,1,1.25,1.5,2,2.5,3];
function fitS(page,w,h){const v=page.getViewport({scale:1});return Math.min(w/v.width,h/v.height)}
async function rp(canvas,ctx,pn,mw,mh){const page=await pdf.getPage(pn);let s=userZoom>0?userZoom:fitS(page,mw,mh);const vp=page.getViewport({scale:s});canvas.width=vp.width;canvas.height=vp.height;await page.render({canvasContext:ctx,viewport:vp}).promise}
async function render(){if(rendering||!pdf)return;rendering=true;const wrap=document.getElementById("canvasWrap");const ww=wrap.clientWidth-16,wh=wrap.clientHeight-16;
c1.style.display="block";
if(spread&&pdf.numPages>1){const hw=(ww/2)-4;c2.style.display="block";await rp(c1,ctx1,pg,hw,wh);const p2=pg+1<=pdf.numPages?pg+1:null;if(p2)await rp(c2,ctx2,p2,hw,wh);else c2.style.display="none";info.textContent=pg+(p2?" - "+p2:"")+" of "+pdf.numPages;prevBtn.disabled=pg<=1;nextBtn.disabled=(p2||pg)>=pdf.numPages}
else{c2.style.display="none";await rp(c1,ctx1,pg,ww,wh);info.textContent=pg+" of "+pdf.numPages;prevBtn.disabled=pg<=1;nextBtn.disabled=pg>=pdf.numPages}
zoomInfo.textContent=userZoom>0?Math.round(userZoom*100)+"%":"Fit";rendering=false}
function step(d){if(spread){const s=d>0?2:-2;const n=pg+s;if(n>=1&&n<=pdf.numPages)pg=n;else if(d>0&&pg+1<pdf.numPages)pg=pdf.numPages}else pg+=d;pg=Math.max(1,Math.min(pg,pdf.numPages));render()}
prevBtn.onclick=()=>step(-1);nextBtn.onclick=()=>step(1);
zoomInBtn.onclick=()=>{const c=userZoom||1;userZoom=ZS.find(z=>z>c+.01)||ZS[ZS.length-1];render()};
zoomOutBtn.onclick=()=>{const c=userZoom||1;userZoom=[...ZS].reverse().find(z=>z<c-.01)||ZS[0];render()};
zoomFitBtn.onclick=()=>{userZoom=0;render()};
spreadBtn.onclick=()=>{spread=!spread;spreadBtn.classList.toggle("active",spread);if(spread&&pg%2===0)pg=Math.max(1,pg-1);render()};
document.addEventListener("keydown",e=>{if(e.key==="ArrowLeft")step(-1);if(e.key==="ArrowRight")step(1);if(e.key==="+"||e.key==="=")zoomInBtn.click();if(e.key==="-")zoomOutBtn.click();if(e.key==="0")zoomFitBtn.click()});
async function loadPdf(src){try{pdf=await getDocument(src).promise;loadEl.style.display="none";render()}catch(e){loadEl.textContent="Failed to load PDF";console.error(e)}}
window.addEventListener("message",async(ev)=>{if(ev.data&&ev.data.type==="pdf-data"&&ev.data.dataUrl){try{const resp=await fetch(ev.data.dataUrl);const buf=await resp.arrayBuffer();loadPdf({data:buf})}catch(e){loadEl.textContent="Failed to load PDF";console.error(e)}}});
window.parent.postMessage({type:"pdf-viewer-ready"},"*");
<\/script></body></html>`;
}

/** PDF lightbox iframe that pre-fetches the PDF with auth cookies and sends data via postMessage */
function PdfLightboxIframe({ url, fileName }: { url: string; fileName: string }) {
  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  const [pdfDataUrl, setPdfDataUrl] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    fetch(url, { credentials: 'include' })
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        const reader = new FileReader();
        reader.onload = () => {
          if (!cancelled) setPdfDataUrl(reader.result as string);
        };
        reader.readAsDataURL(blob);
      })
      .catch((err) => console.error('[PdfLightboxIframe] fetch failed:', err));
    return () => { cancelled = true; };
  }, [url]);

  React.useEffect(() => {
    if (!pdfDataUrl) return;
    const handler = (ev: MessageEvent) => {
      if (ev.data?.type === 'pdf-viewer-ready') {
        iframeRef.current?.contentWindow?.postMessage({ type: 'pdf-data', dataUrl: pdfDataUrl }, '*');
      }
    };
    window.addEventListener('message', handler);
    // Also try sending immediately in case iframe is already ready
    const t = setTimeout(() => {
      iframeRef.current?.contentWindow?.postMessage({ type: 'pdf-data', dataUrl: pdfDataUrl }, '*');
    }, 500);
    return () => { window.removeEventListener('message', handler); clearTimeout(t); };
  }, [pdfDataUrl]);

  return (
    <iframe
      ref={iframeRef}
      srcDoc={buildPdfLightboxSrcDoc(url)}
      title={`PDF: ${fileName}`}
      sandbox="allow-scripts allow-same-origin"
      style={{ width: '100%', height: '80vh', border: 'none', borderRadius: '6px', background: '#111' }}
    />
  );
}

/** Recursive folder tree item — expands like Finder/Explorer */
function FolderSidebarItem({ folder, depth, currentFolderId, onSelect, onRename, onDelete }: {
  folder: any;
  depth: number;
  currentFolderId: string | null;
  onSelect: (id: string, name: string) => void;
  onRename?: (id: string, newName: string) => void;
  onDelete?: (id: string) => void;
}) {
  const [expanded, setExpanded] = useState(depth === 0);
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(folder.name);
  const editRef = useRef<HTMLInputElement>(null);
  const isActive = currentFolderId === folder.id;
  const hasChildren = folder.children && folder.children.length > 0;
  const pl = 8 + depth * 14;

  useEffect(() => {
    if (editing) editRef.current?.select();
  }, [editing]);

  const commitRename = () => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== folder.name) onRename?.(folder.id, trimmed);
    setEditing(false);
  };

  return (
    <div>
      <div
        style={{
          padding: `4px 8px 4px ${pl}px`,
          cursor: 'pointer',
          fontWeight: isActive ? 600 : 400,
          fontSize: '0.82rem',
          color: isActive ? 'var(--primary, #f97316)' : 'var(--foreground)',
          background: isActive ? 'color-mix(in srgb, var(--primary) 8%, transparent)' : 'transparent',
          borderRadius: 4,
          margin: '0 4px 1px',
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          userSelect: 'none',
          position: 'relative',
        }}
        className="folder-sidebar-row"
      >
        <span
          onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
          style={{ width: 14, textAlign: 'center', flexShrink: 0, opacity: hasChildren ? 0.6 : 0, fontSize: '0.65rem', cursor: 'pointer' }}
        >
          {expanded ? '▾' : '▸'}
        </span>
        {editing ? (
          <input
            ref={editRef}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={commitRename}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitRename();
              if (e.key === 'Escape') { setEditValue(folder.name); setEditing(false); }
            }}
            onClick={(e) => e.stopPropagation()}
            style={{
              flex: 1, minWidth: 0, background: 'transparent', border: '1px solid var(--border)',
              borderRadius: 3, padding: '1px 4px', outline: 'none', fontSize: 'inherit', color: 'inherit',
            }}
            autoFocus
          />
        ) : (
          <span
            onClick={() => { onSelect(folder.id, folder.name); if (!expanded) setExpanded(true); }}
            onDoubleClick={(e) => { e.stopPropagation(); setEditValue(folder.name); setEditing(true); }}
            style={{ display: 'flex', alignItems: 'center', gap: 5, flex: 1, minWidth: 0 }}
            title="Double-click to rename"
          >
            {expanded ? <FolderOpen size={14} style={{ color: folder.color || 'var(--primary)', flexShrink: 0 }} /> : <Folder size={14} style={{ color: folder.color || 'var(--muted-foreground)', flexShrink: 0 }} />}
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{folder.name}</span>
          </span>
        )}
        {!editing && folder.assetCount > 0 && (
          <span style={{ fontSize: '0.7rem', opacity: 0.4, flexShrink: 0 }}>{folder.assetCount}</span>
        )}
        {!editing && onDelete && (
          <span
            className="folder-actions"
            style={{
              display: 'none', position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)',
              gap: 2, alignItems: 'center',
            }}
          >
            <button
              onClick={(e) => { e.stopPropagation(); setEditValue(folder.name); setEditing(true); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--muted-foreground)', display: 'flex' }}
              title="Rename"
            >
              <Pencil size={11} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(folder.id); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--destructive, #ef4444)', display: 'flex' }}
              title="Delete"
            >
              <Trash2 size={11} />
            </button>
          </span>
        )}
      </div>
      {expanded && hasChildren && folder.children.map((child: any) => (
        <FolderSidebarItem
          key={child.id}
          folder={child}
          depth={depth + 1}
          currentFolderId={currentFolderId}
          onSelect={onSelect}
          onRename={onRename}
          onDelete={onDelete}
        />
      ))}
      <style>{`.folder-sidebar-row:hover .folder-actions { display: flex !important; }`}</style>
    </div>
  );
}

export const ChatAttachmentsLibrary: React.FC<ChatAttachmentsLibraryProps> = ({
  sessionId,
  isOpen,
  onClose,
  onSelectAttachment,
  variant = 'modal',
  onImageEnhance,
  onRemoveBackground,
  onSendToChat,
}) => {
  const assetsOnly = variant === 'inline';
  const queryClient = useQueryClient();
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [activeTab, setActiveTab] = useState<TabType>('attachments');
  const [previewAttachment, setPreviewAttachment] = useState<ChatAttachmentItem | null>(null);
  const [assetSearch, setAssetSearch] = useState('');
  const [assetTypeFilter, setAssetTypeFilter] = useState<AssetTypeFilter>('all');
  const [showPdfEditor, setShowPdfEditor] = useState(false);
  const [pdfEditorUrl, setPdfEditorUrl] = useState('');
  const [docxLoading, setDocxLoading] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    try { return (localStorage.getItem('asset-view-mode') as ViewMode) || 'large-thumbs'; } catch { return 'large-thumbs'; }
  });
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [folderBreadcrumbs, setFolderBreadcrumbs] = useState<Array<{ id: string; name: string }>>([]);
  const [mobileFoldersOpen, setMobileFoldersOpen] = useState(false);
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [showShareModal, setShowShareModal] = useState<string | null>(null); // assetId for share modal

  useEffect(() => {
    if (!previewAttachment) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [previewAttachment]);

  // GitHub state
  const [selectedRepo, setSelectedRepo] = useState<string | null>(null);
  const [selectedBranch, setSelectedBranch] = useState<string | null>(null);
  const [currentPath, setCurrentPath] = useState<string>('');
  const [repoSearch, setRepoSearch] = useState('');
  const [importingFiles, setImportingFiles] = useState<Set<string>>(new Set());

  const assetFilterOptions: AssetFilterOption[] = [
    { value: 'all', label: 'All', icon: <FolderOpen size={14} /> },
    { value: 'image', label: 'Images', icon: <ImageIcon size={14} /> },
    { value: 'video', label: 'Video', icon: <Video size={14} /> },
    { value: 'audio', label: 'Audio', icon: <FileAudio size={14} /> },
    { value: '3d', label: '3D', icon: <Box size={14} /> },
    { value: 'document', label: 'Docs', icon: <FileText size={14} /> },
  ];
  const effectiveTab: TabType = assetsOnly ? 'generated' : activeTab;

  // GitHub hooks
  const { data: integrations } = useUserIntegrations();
  const { connect: connectGitHub } = useConnectGitHub();
  const hasGithubIntegration = integrations?.integrations?.some((i) => i.provider === 'github');
  const {
    data: reposData,
    isLoading: isLoadingRepos,
    error: reposError,
  } = useGithubRepos(1, 100, hasGithubIntegration && effectiveTab === 'github');

  // Handle GitHub token errors
  useEffect(() => {
    if (reposError) {
      const message = reposError instanceof Error ? reposError.message : String(reposError);
      const missingIntegration =
        message.includes('GitHub integration not found') ||
        message.includes('GitHub token not found') ||
        message.includes('GitHub App not configured on server');
      if (missingIntegration) {
        return;
      }
      console.error('Failed to fetch GitHub repos:', reposError);
    }
  }, [reposError]);
  const { data: branchesData } = useGithubBranches(selectedRepo || undefined, !!selectedRepo);

  // Fetch directory contents
  const { data: contentsData, isLoading: isLoadingContents } = useQuery({
    queryKey: ['github', 'contents', selectedRepo, currentPath, selectedBranch],
    queryFn: () =>
      getGithubContents({
        repo: selectedRepo!,
        path: currentPath,
        branch: selectedBranch || undefined,
      }),
    enabled: !!selectedRepo && effectiveTab === 'github',
  });

  const filteredRepos = reposData?.repos?.filter((repo) =>
    repo.full_name.toLowerCase().includes(repoSearch.toLowerCase()),
  );

  const getAssetType = useCallback((fileName: string, fileType: string | null): AssetTypeFilter => {
    const lowerName = fileName.toLowerCase();

    // Image detection — MIME type OR file extension
    if (fileType?.startsWith('image/')) return 'image';
    if (
      lowerName.endsWith('.png') ||
      lowerName.endsWith('.jpg') ||
      lowerName.endsWith('.jpeg') ||
      lowerName.endsWith('.gif') ||
      lowerName.endsWith('.webp') ||
      lowerName.endsWith('.svg') ||
      lowerName.endsWith('.ico') ||
      lowerName.endsWith('.bmp') ||
      lowerName.endsWith('.avif')
    ) {
      return 'image';
    }
    if (fileType?.startsWith('video/')) return 'video';
    if (fileType?.startsWith('audio/')) return 'audio';
    if (
      fileType === 'model/gltf-binary' ||
      fileType === 'model/gltf+json' ||
      lowerName.endsWith('.glb') ||
      lowerName.endsWith('.gltf') ||
      lowerName.endsWith('.obj') ||
      lowerName.endsWith('.fbx') ||
      lowerName.endsWith('.usdz')
    ) {
      return '3d';
    }
    if (
      fileType?.startsWith('text/') ||
      fileType === 'application/json' ||
      fileType === 'application/pdf' ||
      fileType === 'application/msword' ||
      fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      fileType === 'application/vnd.ms-excel' ||
      fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      fileType === 'application/vnd.ms-powerpoint' ||
      fileType ===
        'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
      lowerName.endsWith('.md') ||
      lowerName.endsWith('.txt') ||
      lowerName.endsWith('.pdf') ||
      lowerName.endsWith('.doc') ||
      lowerName.endsWith('.docx') ||
      lowerName.endsWith('.xls') ||
      lowerName.endsWith('.xlsx') ||
      lowerName.endsWith('.ppt') ||
      lowerName.endsWith('.pptx')
    ) {
      return 'document';
    }
    return 'other';
  }, []);

  const matchesAssetFilter = useCallback(
    ({
      fileName,
      fileType,
      prompt,
      provider,
    }: {
      fileName: string;
      fileType: string | null;
      prompt?: string | null;
      provider?: string | null;
    }) => {
      const type = getAssetType(fileName, fileType);
      const query = assetSearch.trim().toLowerCase();
      const matchesType = assetTypeFilter === 'all' || type === assetTypeFilter;
      const matchesSearch =
        !query ||
        fileName.toLowerCase().includes(query) ||
        (prompt || '').toLowerCase().includes(query) ||
        (provider || '').toLowerCase().includes(query) ||
        type.includes(query);

      return matchesType && matchesSearch;
    },
    [assetSearch, assetTypeFilter, getAssetType],
  );

  // Fetch attachments (use direct fetch for __all__ to avoid schema mismatch)
  const { data, isLoading } = useQuery({
    queryKey: ['chat-attachments', 'list', sessionId],
    queryFn: async () => {
      if (sessionId === '__all__') {
        const res = await fetch(`/_api/chat-attachments/list?sessionId=__all__`, { credentials: 'include' });
        if (!res.ok) return { attachments: [] };
        const text = await res.text();
        try { return superjson.parse<{ attachments: any[] }>(text); }
        catch { try { return JSON.parse(text); } catch { return { attachments: [] }; } }
      }
      return getChatAttachmentsList({ sessionId });
    },
    enabled: isOpen && !!sessionId,
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
    retry: 1,
    staleTime: 10_000,
  });

  // Fetch user generated media with pagination — loads first page, polls for new items
  const [mediaOffset, setMediaOffset] = useState(0);
  const [allMediaItems, setAllMediaItems] = useState<UserMediaItem[]>([]);
  const [hasMoreMedia, setHasMoreMedia] = useState(true);
  const [mediaTotal, setMediaTotal] = useState(0);
  const [mediaError, setMediaError] = useState(false);

  const { data: mediaData, isLoading: isLoadingMedia } = useQuery({
    queryKey: ['user-media', 'list', mediaOffset],
    queryFn: () => fetchUserMedia(mediaOffset),
    enabled: isOpen && effectiveTab === 'generated',
    refetchInterval: mediaOffset === 0 && !mediaError ? 15_000 : false, // only poll first page, stop on error
    refetchIntervalInBackground: false,
    retry: 1,
    staleTime: 10_000,
  });

  // Accumulate pages — stop on errors or when offset exceeds total
  useEffect(() => {
    if (!mediaData) return;
    // Stop pagination on server errors
    if ((mediaData as any).error) {
      setHasMoreMedia(false);
      setMediaError(true);
      return;
    }
    if (mediaData?.items) {
      if (mediaOffset === 0) {
        setAllMediaItems(mediaData.items);
      } else {
        setAllMediaItems(prev => {
          const existingIds = new Set(prev.map(i => i.id));
          const newItems = mediaData.items.filter((i: UserMediaItem) => !existingIds.has(i.id));
          return [...prev, ...newItems];
        });
      }
      setHasMoreMedia(mediaData.hasMore ?? false);
      setMediaTotal(mediaData.total ?? 0);
    }
  }, [mediaData, mediaOffset]);

  const loadMoreMedia = useCallback(() => {
    if (hasMoreMedia && !mediaError && mediaOffset + MEDIA_PAGE_SIZE < mediaTotal + MEDIA_PAGE_SIZE) {
      setMediaOffset(prev => prev + MEDIA_PAGE_SIZE);
    }
  }, [hasMoreMedia, mediaError, mediaOffset, mediaTotal]);

  // Fetch asset folders for current level
  const { data: foldersData, isLoading: isLoadingFolders } = useQuery({
    queryKey: ['asset-folders', currentFolderId],
    queryFn: async () => {
      const res = await fetch('/_api/user-media/folders', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'list', parentId: currentFolderId }),
      });
      if (!res.ok) return { folders: [] };
      return res.json();
    },
    enabled: isOpen && effectiveTab === 'generated',
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
    retry: 1,
    staleTime: 10_000,
  });

  // Fetch ALL folders (flat list) and build tree client-side
  const { data: allFoldersFlat } = useQuery({
    queryKey: ['asset-folders-all'],
    queryFn: async () => {
      const res = await fetch('/_api/user-media/folders', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'list', parentId: '__all__' }),
      });
      if (!res.ok) return [];
      const data = await res.json();
      return data.folders || [];
    },
    enabled: isOpen && effectiveTab === 'generated',
    staleTime: 30_000,
    retry: 1,
  });

  // Build folder tree from flat list
  const allFoldersData = useMemo(() => {
    if (!allFoldersFlat || allFoldersFlat.length === 0) return [];
    const byParent = new Map<string | null, any[]>();
    for (const f of allFoldersFlat) {
      const parentId = f.parentId || null;
      if (!byParent.has(parentId)) byParent.set(parentId, []);
      byParent.get(parentId)!.push(f);
    }
    const buildTree = (parentId: string | null): any[] => {
      const children = byParent.get(parentId) || [];
      return children.map(f => ({ ...f, children: buildTree(f.id) }));
    };
    return buildTree(null);
  }, [allFoldersFlat]);

  const handleCreateFolder = useCallback(async () => {
    if (!newFolderName.trim()) return;
    try {
      await fetch('/_api/user-media/folders', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'create', name: newFolderName.trim(), parentId: currentFolderId }),
      });
      queryClient.invalidateQueries({ queryKey: ['asset-folders'] });
      setNewFolderName('');
      setShowCreateFolder(false);
    } catch (e: any) {
      toast.error(e.message || 'Failed to create folder');
    }
  }, [newFolderName, currentFolderId, queryClient]);

  const handleRenameFolder = useCallback(async (folderId: string, newName: string) => {
    try {
      await fetch('/_api/user-media/folders', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'rename', folderId, name: newName }),
      });
      queryClient.invalidateQueries({ queryKey: ['asset-folders'] });
      toast.success('Folder renamed');
    } catch (e: any) {
      toast.error(e.message || 'Failed to rename folder');
    }
  }, [queryClient]);

  const handleDeleteFolder = useCallback(async (folderId: string) => {
    if (!confirm('Delete this folder? Contents will be moved to the parent folder.')) return;
    try {
      await fetch('/_api/user-media/folders', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'delete', folderId }),
      });
      queryClient.invalidateQueries({ queryKey: ['asset-folders'] });
      toast.success('Folder deleted');
    } catch (e: any) {
      toast.error(e.message || 'Failed to delete folder');
    }
  }, [queryClient]);

  const handleMoveAssetToFolder = useCallback(async (assetId: string, folderId: string | null, assetType: 'media' | 'attachment' = 'media') => {
    try {
      await fetch('/_api/user-media/folders', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'move-asset', assetId, folderId, assetType }),
      });
      queryClient.invalidateQueries({ queryKey: ['user-media'] });
      queryClient.invalidateQueries({ queryKey: ['asset-folders'] });
      toast.success('Asset moved');
    } catch (e: any) {
      toast.error(e.message || 'Failed to move asset');
    }
  }, [queryClient]);

  const handleMoveFolderToFolder = useCallback(async (folderId: string, newParentId: string | null) => {
    if (folderId === newParentId) return;
    try {
      const res = await fetch('/_api/user-media/folders', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'move', folderId, newParentId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to move folder');
      queryClient.invalidateQueries({ queryKey: ['asset-folders'] });
      toast.success('Folder moved');
    } catch (e: any) {
      toast.error(e.message || 'Failed to move folder');
    }
  }, [queryClient]);

  const navigateToFolder = useCallback((folderId: string, folderName: string) => {
    setFolderBreadcrumbs(prev => [...prev, { id: folderId, name: folderName }]);
    setCurrentFolderId(folderId);
  }, []);

  const navigateToBreadcrumb = useCallback((index: number) => {
    if (index < 0) {
      setFolderBreadcrumbs([]);
      setCurrentFolderId(null);
    } else {
      const crumb = folderBreadcrumbs[index];
      setFolderBreadcrumbs(prev => prev.slice(0, index + 1));
      setCurrentFolderId(crumb.id);
    }
  }, [folderBreadcrumbs]);

  const filteredAttachments =
    data?.attachments?.filter((attachment) =>
      matchesAssetFilter({
        fileName: attachment.fileName,
        fileType: attachment.fileType,
      }),
    ) || [];

  const filteredMediaItemsRaw =
    allMediaItems?.filter((item) => {
      // Filter by current folder
      const itemFolder = item.folderId || null;
      if (itemFolder !== currentFolderId) return false;
      return matchesAssetFilter({
        fileName: item.fileName,
        fileType: item.fileType,
        prompt: item.prompt,
        provider: item.provider,
      });
    }) || [];

  // When assetsOnly (inline), merge uploaded chat attachments into the media view.
  // Deduplicate by URL to avoid showing the same item twice (userMedia + chatAttachments overlap).
  const filteredMediaItems = (() => {
    if (!assetsOnly) return filteredMediaItemsRaw;
    const seenUrls = new Set(filteredMediaItemsRaw.map(i => i.url));
    const extraFromAttachments = filteredAttachments
      .filter(a => !seenUrls.has(a.dataUrl))
      .filter(a => {
        // Only show attachments at root level (they don't have folder support)
        return currentFolderId === null;
      })
      .map((a) => ({
        id: a.id,
        fileName: a.fileName,
        fileType: a.fileType,
        fileSize: a.fileSize,
        url: a.dataUrl,
        thumbnailUrl: null,
        source: 'upload' as const,
        prompt: null,
        provider: null,
        r2Key: null,
        metadata: null,
        createdAt: a.createdAt,
        folderId: null,
      }));
    return [...filteredMediaItemsRaw, ...extraFromAttachments]
      .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());
  })();

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: uploadChatAttachment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-attachments', 'list', sessionId] });
      toast.success('File uploaded to library');
    },
    onError: (error: Error) => {
      toast.error(`Upload failed: ${error.message}`);
    },
  });

  // Share mutation
  const shareMutation = useMutation({
    mutationFn: shareChatAttachment,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['chat-attachments', 'list', sessionId] });
      if (data.shareUrl) {
        const fullUrl = `${window.location.origin}${data.shareUrl}`;
        navigator.clipboard.writeText(fullUrl);
        toast.success('Share link copied to clipboard');
      } else {
        toast.success('Attachment is now private');
      }
    },
    onError: (error: Error) => {
      toast.error(`Share failed: ${error.message}`);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: deleteChatAttachment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-attachments', 'list', sessionId] });
      toast.success('Attachment deleted');
    },
    onError: (error: Error) => {
      toast.error(`Delete failed: ${error.message}`);
    },
  });

  // Delete user media mutation
  const deleteMediaMutation = useMutation({
    mutationFn: (id: string) => deleteUserMedia(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-media', 'list'] });
      toast.success('Media deleted');
    },
    onError: (error: Error) => {
      toast.error(`Delete failed: ${error.message}`);
    },
  });

  // Handle file upload — uses direct binary upload for large files
  const handleFileUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      try {
        const result = await uploadChatAttachmentSmart(file, {
          sessionId,
          fileName: file.name,
          fileType: file.type,
        });
        queryClient.invalidateQueries({ queryKey: ['chat-attachments', 'list', sessionId] });
        toast.success('File uploaded to library');
      } catch (error: any) {
        toast.error(`Upload failed: ${error?.message || 'Unknown error'}`);
      }
      e.target.value = '';
    },
    [sessionId, queryClient],
  );

  // Handle toggle share
  const handleToggleShare = useCallback(
    (attachment: ChatAttachmentItem) => {
      shareMutation.mutate({
        attachmentId: attachment.id,
        isPrivate: !attachment.isPrivate,
      });
    },
    [shareMutation],
  );

  // Handle copy share link
  const handleCopyLink = useCallback((attachment: ChatAttachmentItem) => {
    if (attachment.shareToken) {
      const fullUrl = `${window.location.origin}/_api/chat-attachments/shared?token=${attachment.shareToken}`;
      navigator.clipboard.writeText(fullUrl);
      setCopiedId(attachment.id);
      toast.success('Link copied');
      setTimeout(() => setCopiedId(null), 2000);
    }
  }, []);

  // Handle delete
  const handleDelete = useCallback(
    (attachmentId: string) => {
      if (window.confirm('Delete this attachment?')) {
        deleteMutation.mutate({ attachmentId });
      }
    },
    [deleteMutation],
  );

  const handleDownload = useCallback(async (attachment: ChatAttachmentItem) => {
    try {
      const response = await fetch(attachment.dataUrl);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download attachment:', error);
      toast.error('Could not download this attachment');
    }
  }, []);

  const handleOpenPreviewInNewTab = useCallback((attachment: ChatAttachmentItem) => {
    window.open(attachment.dataUrl, '_blank', 'noopener,noreferrer');
  }, []);

  // Get icon for file type
  const getFileIcon = (fileType: string | null) => {
    if (fileType?.startsWith('image/')) {
      return <ImageIcon className={styles.fileIcon} />;
    }
    if (fileType?.startsWith('video/')) {
      return <Video className={styles.fileIcon} />;
    }
    if (fileType?.startsWith('audio/')) {
      return <FileAudio className={styles.fileIcon} />;
    }
    if (fileType === 'model/gltf-binary' || fileType === 'model/gltf+json') {
      return <Box className={styles.fileIcon} />;
    }
    if (fileType?.startsWith('text/') || fileType === 'application/json') {
      return <FileText className={styles.fileIcon} />;
    }
    return <File className={styles.fileIcon} />;
  };

  // Get icon for user media item
  const getMediaIcon = (item: UserMediaItem) => {
    if (item.fileType?.startsWith('image/')) return <ImageIcon className={styles.fileIcon} />;
    if (item.fileType?.startsWith('video/')) return <Video className={styles.fileIcon} />;
    if (item.fileType?.startsWith('audio/')) return <FileAudio className={styles.fileIcon} />;
    if (item.fileType === 'model/gltf-binary' || item.fileName?.endsWith('.glb'))
      return <Box className={styles.fileIcon} />;
    if (item.fileType === 'text/html') return <FileText className={styles.fileIcon} />;
    return <Sparkles className={styles.fileIcon} />;
  };

  // Handle delete user media
  const handleDeleteMedia = useCallback(
    (mediaId: string) => {
      if (window.confirm('Delete this generated media?')) {
        deleteMediaMutation.mutate(mediaId);
      }
    },
    [deleteMediaMutation],
  );

  // Handle download user media
  const handleDownloadMedia = useCallback(async (item: UserMediaItem) => {
    try {
      const response = await fetch(item.url);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = item.fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download media:', error);
      toast.error('Could not download this media');
    }
  }, []);

  // Get icon for GitHub file/folder
  const getGitHubIcon = (type: string, name: string) => {
    if (type === 'dir') {
      return <Folder className={styles.fileIcon} />;
    }
    const ext = name.split('.').pop()?.toLowerCase();
    if (['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp'].includes(ext || '')) {
      return <ImageIcon className={styles.fileIcon} />;
    }
    if (['js', 'ts', 'jsx', 'tsx', 'py', 'json', 'md', 'txt', 'css', 'html'].includes(ext || '')) {
      return <FileText className={styles.fileIcon} />;
    }
    return <File className={styles.fileIcon} />;
  };

  // Handle repo selection
  const handleSelectRepo = (repoFullName: string, defaultBranch: string) => {
    setSelectedRepo(repoFullName);
    setSelectedBranch(defaultBranch);
    setCurrentPath('');
  };

  // Handle navigation
  const handleNavigate = (item: { type: string; name: string; path: string }) => {
    if (item.type === 'dir') {
      setCurrentPath(item.path);
    }
  };

  // Handle back navigation
  const handleGoBack = () => {
    if (currentPath) {
      const parts = currentPath.split('/');
      parts.pop();
      setCurrentPath(parts.join('/'));
    } else {
      setSelectedRepo(null);
      setSelectedBranch(null);
    }
  };

  // Import file from GitHub to attachments
  const handleImportFile = async (filePath: string, fileName: string) => {
    if (!selectedRepo || !selectedBranch) return;

    setImportingFiles((prev) => new Set(prev).add(filePath));

    try {
      const fileData = await getGithubFile({
        repo: selectedRepo,
        path: filePath,
        branch: selectedBranch,
      });

      // Determine file type from extension
      const ext = fileName.split('.').pop()?.toLowerCase();
      let fileType = 'text/plain';
      if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext || '')) {
        fileType = `image/${ext === 'jpg' ? 'jpeg' : ext}`;
      } else if (ext === 'svg') {
        fileType = 'image/svg+xml';
      } else if (ext === 'json') {
        fileType = 'application/json';
      } else if (['js', 'ts', 'jsx', 'tsx'].includes(ext || '')) {
        fileType = 'text/javascript';
      } else if (ext === 'css') {
        fileType = 'text/css';
      } else if (ext === 'html') {
        fileType = 'text/html';
      } else if (ext === 'md') {
        fileType = 'text/markdown';
      }

      // Convert content to data URL
      const dataUrl = fileType.startsWith('image/')
        ? `data:${fileType};base64,${btoa(fileData.content)}`
        : `data:${fileType};base64,${btoa(unescape(encodeURIComponent(fileData.content)))}`;

      await uploadMutation.mutateAsync({
        sessionId,
        fileName,
        fileType,
        fileSize: fileData.content.length,
        dataUrl,
      });

      toast.success(`Imported ${fileName} to library`);
    } catch (error) {
      console.error('Failed to import file:', error);
      toast.error(`Failed to import ${fileName}`);
    } finally {
      setImportingFiles((prev) => {
        const next = new Set(prev);
        next.delete(filePath);
        return next;
      });
    }
  };

  // Format file size
  const formatSize = (bytes: number | null) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatTimestamp = (dateStr: string | null) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDays = Math.floor(diffHr / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  const getAssetTypeLabel = (fileName: string, fileType: string | null) => {
    const type = getAssetType(fileName, fileType);
    if (type === '3d') return '3D';
    if (type === 'document') return 'Document';
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const formatFullDate = (dateStr: string | null) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getGridStyle = (): React.CSSProperties => {
    switch (viewMode) {
      case 'small-thumbs':
        return { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '8px' };
      case 'large-thumbs':
        return { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' };
      case 'tiled':
        return { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' };
      case 'details':
        return { display: 'flex', flexDirection: 'column', gap: '4px' };
      default:
        return {};
    }
  };

  const renderAssetItem = (
    key: string,
    preview: React.ReactNode,
    rawFileName: string,
    fileType: string | null,
    fileSize: number | null,
    createdAt: string | null,
    provider: string | null,
    prompt: string | null,
    actions: React.ReactNode,
    onClick: () => void,
    onDragStart?: (e: React.DragEvent) => void,
    assetId?: string | null,
    assetType?: 'media' | 'attachment',
    assetUrl?: string,
  ) => {
    // Sanitize: strip HTML from filename and prompt
    let fileName = rawFileName;
    if (fileName && /<[a-z][\s\S]*>/i.test(fileName)) {
      const stripped = fileName.replace(/<[^>]+>/g, '').trim();
      fileName = stripped.length > 3 ? stripped.slice(0, 60) : 'Document.pdf';
    }
    // For display label: prefer clean fileName, never show raw HTML prompt
    const displayName = fileName || 'Untitled';
    const cleanPrompt = prompt && !/<[a-z]/i.test(prompt) ? prompt : null;
    const typeLabel = getAssetTypeLabel(fileName, fileType);

    const contextMenu = assetId ? (
      <ContextMenuContent>
        <ContextMenuSub>
          <ContextMenuSubTrigger><FolderInput size={14} style={{ marginRight: 8 }} />Move to Folder</ContextMenuSubTrigger>
          <ContextMenuSubContent>
            <ContextMenuItem onClick={() => handleMoveAssetToFolder(assetId, null, assetType || 'media')}>
              No folder
            </ContextMenuItem>
            {allFoldersFlat?.map((f: any) => (
              <ContextMenuItem key={f.id} onClick={() => handleMoveAssetToFolder(assetId, f.id, assetType || 'media')}>
                <Folder size={12} style={{ marginRight: 6 }} />{f.name}
              </ContextMenuItem>
            ))}
          </ContextMenuSubContent>
        </ContextMenuSub>
        <ContextMenuSeparator />
        <ContextMenuItem onClick={onClick}><Eye size={14} style={{ marginRight: 8 }} />Preview</ContextMenuItem>
        <ContextMenuItem onClick={() => {
          if (!assetUrl) return;
          const a = document.createElement('a');
          a.href = assetUrl;
          a.download = displayName;
          a.target = '_blank';
          a.click();
        }}><Download size={14} style={{ marginRight: 8 }} />Download</ContextMenuItem>
        <ContextMenuItem onClick={() => { if (assetId) setShowShareModal(assetId); }}><Link2 size={14} style={{ marginRight: 8 }} />Share</ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem onClick={() => { if (assetId) handleDeleteMedia(assetId); }} style={{ color: '#ef4444' }}>
          <Trash2 size={14} style={{ marginRight: 8 }} />Delete
        </ContextMenuItem>
      </ContextMenuContent>
    ) : null;

    const wrapContextMenu = (el: React.ReactNode) => {
      if (!assetId || !contextMenu) return el;
      return (
        <ContextMenu key={key}>
          <ContextMenuTrigger asChild>{el}</ContextMenuTrigger>
          {contextMenu}
        </ContextMenu>
      );
    };

    if (viewMode === 'details') {
      return wrapContextMenu(
        <div
          key={key}
          onClick={onClick}
          onDragStart={onDragStart}
          draggable={!!onDragStart}
          style={{
            display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 12px',
            background: 'var(--background)', border: '1px solid var(--border)', borderRadius: '8px',
            cursor: 'pointer', transition: 'all 0.15s ease',
          }}
          onMouseOver={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--primary)'; }}
          onMouseOut={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--border)'; }}
        >
          <div style={{ width: 40, height: 40, borderRadius: '6px', overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--muted)' }}>
            {preview}
          </div>
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <span style={{ fontSize: '0.8rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={cleanPrompt || displayName}>
              {cleanPrompt ? (cleanPrompt.length > 60 ? `${cleanPrompt.slice(0, 57)}...` : cleanPrompt) : displayName}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.68rem', color: 'var(--muted-foreground)' }}>
              <span className={styles.typeTag} style={{ fontSize: '0.58rem' }}>{typeLabel}</span>
              {fileSize ? <span>{formatSize(fileSize)}</span> : null}
              {createdAt && (
                <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                  <Calendar size={10} />
                  {formatFullDate(createdAt)}
                </span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '2px', flexShrink: 0 }}>
            {actions}
          </div>
        </div>
      );
    }

    if (viewMode === 'small-thumbs') {
      return wrapContextMenu(
        <div
          key={key}
          onClick={onClick}
          onDragStart={onDragStart}
          draggable={!!onDragStart}
          style={{
            background: 'var(--background)', border: '1px solid var(--border)', borderRadius: '8px',
            padding: '4px', cursor: 'pointer', transition: 'all 0.15s ease', display: 'flex', flexDirection: 'column', gap: '4px',
          }}
          onMouseOver={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--primary)'; }}
          onMouseOut={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--border)'; }}
        >
          <div style={{ width: '100%', aspectRatio: '1', borderRadius: '6px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--muted)' }}>
            {preview}
          </div>
          <span style={{ fontSize: '0.6rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: '0 2px' }} title={displayName}>
            {displayName}
          </span>
        </div>
      );
    }

    if (viewMode === 'tiled') {
      return wrapContextMenu(
        <div
          key={key}
          onClick={onClick}
          onDragStart={onDragStart}
          draggable={!!onDragStart}
          style={{
            background: 'var(--background)', border: '1px solid var(--border)', borderRadius: '10px',
            overflow: 'hidden', cursor: 'pointer', transition: 'all 0.15s ease', display: 'flex', flexDirection: 'column',
          }}
          onMouseOver={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--primary)'; }}
          onMouseOut={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--border)'; }}
        >
          <div style={{ width: '100%', aspectRatio: '16/10', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--muted)' }}>
            {preview}
          </div>
          <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span style={{ fontSize: '0.8rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={cleanPrompt || displayName}>
              {cleanPrompt ? (cleanPrompt.length > 50 ? `${cleanPrompt.slice(0, 47)}...` : cleanPrompt) : displayName}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.68rem', color: 'var(--muted-foreground)', flexWrap: 'wrap' }}>
              <span className={styles.typeTag} style={{ fontSize: '0.58rem' }}>{typeLabel}</span>
              {fileSize ? <span>{formatSize(fileSize)}</span> : null}
              {createdAt && <span>{formatFullDate(createdAt)}</span>}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '2px', marginTop: '4px', borderTop: '1px solid var(--border)', paddingTop: '6px' }}>
              {actions}
            </div>
          </div>
        </div>
      );
    }

    // large-thumbs (default)
    return wrapContextMenu(
      <div
        key={key}
        className={styles.item}
        onClick={onClick}
        onDragStart={onDragStart}
        draggable={!!onDragStart}
      >
        <div className={styles.preview}>
          {preview}
        </div>
        <div className={styles.itemInfo}>
          <span className={styles.typeTag}>{typeLabel}</span>
          <span className={styles.fileName} title={cleanPrompt || displayName}>
            {cleanPrompt ? (cleanPrompt.length > 40 ? `${cleanPrompt.slice(0, 37)}...` : cleanPrompt) : displayName}
          </span>
          <span className={styles.fileSize}>
            {fileSize ? formatSize(fileSize) : ''}
            {createdAt ? ` · ${formatFullDate(createdAt)}` : ''}
          </span>
        </div>
        <div className={styles.itemActions}>
          {actions}
        </div>
      </div>
    );
  };

  const setAndSaveViewMode = useCallback((mode: ViewMode) => {
    setViewMode(mode);
    try { localStorage.setItem('asset-view-mode', mode); } catch {}
  }, []);

  const viewModeOptions: { value: ViewMode; label: string; icon: React.ReactNode }[] = [
    { value: 'small-thumbs', label: 'Small Thumbnails', icon: <Grid3X3 size={14} /> },
    { value: 'large-thumbs', label: 'Large Thumbnails', icon: <LayoutGrid size={14} /> },
    { value: 'tiled', label: 'Tiled', icon: <GalleryHorizontalEnd size={14} /> },
    { value: 'details', label: 'Details', icon: <List size={14} /> },
  ];

  const renderAssetFilterBar = (placeholder: string) => (
    <div className={styles.filterBar}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%' }}>
        {!assetsOnly && (
          <div className={styles.filterSearchWrap}>
            <Input
              placeholder={placeholder}
              value={assetSearch}
              onChange={(e) => setAssetSearch(e.target.value)}
              className={styles.filterSearchInput}
            />
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: '2px', marginLeft: 'auto', background: 'var(--background)', borderRadius: '8px', padding: '2px', border: '1px solid var(--border)' }}>
          {viewModeOptions.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setAndSaveViewMode(opt.value)}
              title={opt.label}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 28, height: 28, border: 'none', borderRadius: '6px', cursor: 'pointer',
                background: viewMode === opt.value ? 'var(--primary)' : 'transparent',
                color: viewMode === opt.value ? '#fff' : 'var(--muted-foreground)',
                transition: 'all 0.15s ease',
              }}
            >
              {opt.icon}
            </button>
          ))}
        </div>
      </div>
      <div className={styles.filterChipRow}>
        {assetFilterOptions.map((option) => (
          <button
            key={option.value}
            type="button"
            className={`${styles.filterChip} ${
              assetTypeFilter === option.value ? styles.filterChipActive : ''
            }`}
            onClick={() => setAssetTypeFilter(option.value)}
            title={option.label}
            aria-label={option.label}
          >
            {option.icon}
          </button>
        ))}
      </div>
    </div>
  );

  // PDF actions for assets lightbox
  const isPdfPreview = previewAttachment && (
    previewAttachment.fileType === 'application/pdf' ||
    previewAttachment.fileName?.toLowerCase().endsWith('.pdf')
  );

  const handleEditPdf = useCallback(() => {
    if (!previewAttachment) return;
    setPdfEditorUrl(previewAttachment.dataUrl);
    setShowPdfEditor(true);
  }, [previewAttachment]);

  const handleDocxDownload = useCallback(async () => {
    if (!previewAttachment) return;
    setDocxLoading(true);
    try {
      const res = await fetch('/_api/taskingbot/pdf-to-docx', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: previewAttachment.dataUrl }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Download failed' }));
        throw new Error(err.error || 'Download failed');
      }
      const blob = await res.blob();
      const docxName = (previewAttachment.fileName || 'document').replace(/\.pdf$/i, '') + '.docx';
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = docxName;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success('DOCX downloaded');
    } catch (e: any) {
      toast.error(e.message || 'DOCX download failed');
    } finally {
      setDocxLoading(false);
    }
  }, [previewAttachment]);

  if (!isOpen) return null;

  const lightbox =
    previewAttachment && typeof document !== 'undefined'
      ? createPortal(
          <div
            className={styles.lightboxOverlay}
            onClick={() => setPreviewAttachment(null)}
            role="dialog"
            aria-modal="true"
            aria-label={previewAttachment.fileName}
          >
            <div className={styles.lightboxBox} onClick={(e) => e.stopPropagation()}>
              <div className={styles.lightboxHeader}>
                <span className={styles.lightboxTitle}>{previewAttachment.fileName}</span>
                <div className={styles.lightboxActions}>
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() => {
                      onSelectAttachment?.(previewAttachment);
                      setPreviewAttachment(null);
                    }}
                    title="Edit in chat"
                    aria-label="Edit in chat"
                  >
                    <MessageSquarePlus size={14} />
                    Edit in chat
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleOpenPreviewInNewTab(previewAttachment)}
                    title="Open full image"
                    aria-label="Open full image"
                  >
                    <ExternalLink size={14} />
                    Open
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownload(previewAttachment)}
                    title="Download"
                    aria-label="Download"
                  >
                    <Download size={14} />
                    Download
                  </Button>
                  {isPdfPreview && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleEditPdf}
                        title="Edit PDF"
                        aria-label="Edit PDF"
                      >
                        <FileText size={14} />
                        Edit PDF
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleDocxDownload}
                        disabled={docxLoading}
                        title="Download as DOCX"
                        aria-label="Download as DOCX"
                        style={{ gap: 4 }}
                      >
                        <svg width={14} height={14} viewBox="0 0 384 512" fill="#2b579a">
                          <path d="M369.9 97.9L286 14C277 5 264.8-.1 252.1-.1H48C21.5 0 0 21.5 0 48v416c0 26.5 21.5 48 48 48h288c26.5 0 48-21.5 48-48V131.9c0-12.7-5.1-25-14.1-34zM332.1 128H256V51.9l76.1 76.1zM48 464V48h160v104c0 13.3 10.7 24 24 24h104v288H48zm220.1-208c-5.7 0-10.6 4-11.7 9.5-20.6 97.7-20.4 95.4-21 103.5-.2-4.6-.4-9.1-1.3-13.5l-25.4-84.2c-2.3-7.6-9.3-12.8-17.2-12.8h-3.3c-8 0-15 5.2-17.2 12.8l-25.4 84.2c-.9 3-1.1 6.1-1.3 9.2-.4-6.7-.2-5.5-21-103.5-1.1-5.5-6-9.5-11.7-9.5H104c-7.6 0-13.3 6.8-11.7 14.2l39.5 163.7c1.5 6.2 7.1 10.6 13.5 10.6h3.7c7.6 0 14.3-5 16.5-12.2l24.9-84.6c.7-2.2 1-4.6 1.4-6.9.5 2.5.8 5.1 1.6 7.5l23.8 84c2.1 7.1 8.7 12.2 16.3 12.2h3.7c6.4 0 12-4.4 13.5-10.6l39.5-163.7c1.6-7.5-4.1-14.3-11.7-14.3h-7.5z" />
                        </svg>
                        {docxLoading ? '...' : 'DOCX'}
                      </Button>
                    </>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { if (previewAttachment) setShowShareModal(previewAttachment.id); }}
                    title="Share"
                    aria-label="Share"
                  >
                    <Link2 size={14} />
                    Share
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setPreviewAttachment(null)}
                    title="Close preview"
                    aria-label="Close preview"
                  >
                    <X size={16} />
                    Close
                  </Button>
                </div>
              </div>
              <div className={styles.lightboxImageWrap}>
                {isGlbFile(previewAttachment.fileName || '') || isGlbFile(previewAttachment.fileType || '') ? (
                  <GlbViewer
                    src={previewAttachment.dataUrl}
                    name={previewAttachment.fileName}
                    compact={false}
                  />
                ) : previewAttachment.fileType === 'application/pdf' || previewAttachment.fileName?.toLowerCase().endsWith('.pdf') ? (
                  <PdfLightboxIframe url={previewAttachment.dataUrl} fileName={previewAttachment.fileName} />
                ) : (
                  <img
                    src={previewAttachment.dataUrl}
                    alt={previewAttachment.fileName}
                    className={styles.lightboxImage}
                  />
                )}
              </div>
              {previewAttachment.fileSize && (
                <div className={styles.lightboxMeta}>{formatSize(previewAttachment.fileSize)}</div>
              )}
            </div>
          </div>,
          document.body,
        )
      : null;

  const panel = (
    <>
      <div
        className={`${styles.panel} ${variant === 'inline' ? styles.inlinePanel : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.header}>
          <div className={styles.headerTitle}>
            <FolderOpen className={styles.headerIcon} />
            <span>My Library</span>
          </div>

          {/* Unified toolbar: upload + type filters + view modes + search */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flex: 1, minWidth: 0, flexWrap: 'nowrap', overflow: 'hidden' }}>
            {/* Upload button */}
            <label
              title="Upload files"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 30, height: 30, borderRadius: 6, cursor: 'pointer', flexShrink: 0,
                border: '1px solid var(--border)', background: 'var(--card)',
                color: 'var(--foreground)', transition: 'all 0.15s',
              }}
            >
              <input
                type="file"
                accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.json,.svg,.zip,.rar"
                multiple
                disabled={uploadMutation.isPending}
                style={{ display: 'none' }}
                onChange={(evt) => {
                  const files = evt.target.files;
                  if (!files?.length) return;
                  const uploadSessionId = sessionId === '__all__' ? 'taskingbot-global' : sessionId;
                  Array.from(files).forEach((file) => {
                    uploadChatAttachmentSmart(file, {
                      sessionId: uploadSessionId,
                      fileName: file.name,
                      fileType: file.type,
                    }).then(() => {
                      queryClient.invalidateQueries({ queryKey: ['chat-attachments', 'list', sessionId] });
                      queryClient.invalidateQueries({ queryKey: ['user-media', 'list'] });
                      toast.success('File uploaded');
                    }).catch((err: any) => {
                      toast.error(`Upload failed: ${err?.message || 'Unknown error'}`);
                    });
                  });
                  evt.target.value = '';
                }}
              />
              {uploadMutation.isPending ? <Loader2 size={14} className={styles.spinning} /> : <Upload size={14} />}
            </label>

            {/* Divider */}
            <div style={{ width: 1, height: 20, background: 'var(--border)', flexShrink: 0 }} />

            {/* Type filter chips */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
              {assetFilterOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setAssetTypeFilter(option.value)}
                  title={option.label}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: 28, height: 28, border: 'none', borderRadius: 6, cursor: 'pointer',
                    background: assetTypeFilter === option.value ? 'var(--primary)' : 'transparent',
                    color: assetTypeFilter === option.value ? '#fff' : 'var(--muted-foreground)',
                    transition: 'all 0.15s', flexShrink: 0,
                  }}
                >
                  {option.icon}
                </button>
              ))}
            </div>

            {/* Divider */}
            <div style={{ width: 1, height: 20, background: 'var(--border)', flexShrink: 0 }} />

            {/* View mode toggles */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 1, background: 'var(--background)', borderRadius: 6, padding: 2, border: '1px solid var(--border)', flexShrink: 0 }}>
              {viewModeOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setAndSaveViewMode(opt.value)}
                  title={opt.label}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: 26, height: 26, border: 'none', borderRadius: 5, cursor: 'pointer',
                    background: viewMode === opt.value ? 'var(--primary)' : 'transparent',
                    color: viewMode === opt.value ? '#fff' : 'var(--muted-foreground)',
                    transition: 'all 0.15s',
                  }}
                >
                  {opt.icon}
                </button>
              ))}
            </div>

            {/* Search */}
            <div style={{ flex: 1, minWidth: 100 }}>
              <Input
                placeholder="Search assets..."
                value={assetSearch}
                onChange={(e) => setAssetSearch(e.target.value)}
                className={styles.headerSearchInput}
              />
            </div>
          </div>

          {variant === 'modal' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className={styles.closeButton}
              title="Close library"
              aria-label="Close library"
            >
              <X size={16} />
            </Button>
          )}
        </div>

        <div className={styles.libraryBody} style={{ display: 'flex', gap: 0 }}>
          {/* Folder sidebar — shown on all screens, adapts via CSS */}
          {effectiveTab === 'generated' && allFoldersData && allFoldersData.length > 0 && (
            <div className={styles.desktopFolderSidebar} style={{
              width: 200, flexShrink: 0, borderRight: '1px solid var(--border, #2a2a2a)',
              overflow: 'auto', padding: '8px 0', fontSize: '0.85rem',
            }}>
              <div
                onClick={() => { setCurrentFolderId(null); setFolderBreadcrumbs([]); }}
                style={{
                  padding: '6px 12px', cursor: 'pointer', fontWeight: currentFolderId === null ? 700 : 400,
                  color: currentFolderId === null ? 'var(--primary, #f97316)' : 'var(--foreground)',
                  background: currentFolderId === null ? 'color-mix(in srgb, var(--primary) 8%, transparent)' : 'transparent',
                  borderRadius: 6, margin: '0 4px 2px',
                }}
              >
                All Files
              </div>
              {allFoldersData.map((folder: any) => (
                <FolderSidebarItem
                  key={folder.id}
                  folder={folder}
                  depth={0}
                  currentFolderId={currentFolderId}
                  onSelect={(id, name) => {
                    setCurrentFolderId(id);
                    setFolderBreadcrumbs([{ id, name }]);
                  }}
                  onRename={handleRenameFolder}
                  onDelete={handleDeleteFolder}
                />
              ))}
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {!assetsOnly && (
            <div className={styles.tabs}>
              <button
                className={`${styles.tab} ${activeTab === 'attachments' ? styles.tabActive : ''}`}
                onClick={() => setActiveTab('attachments')}
                title="Attachments"
                aria-label="Attachments"
              >
                <FolderOpen size={16} />
                <span>Attachments</span>
              </button>
              <button
                className={`${styles.tab} ${activeTab === 'generated' ? styles.tabActive : ''}`}
                onClick={() => setActiveTab('generated')}
                title="Generated assets"
                aria-label="Generated assets"
              >
                <Sparkles size={16} />
                <span>Generated</span>
              </button>
              <button
                className={`${styles.tab} ${activeTab === 'github' ? styles.tabActive : ''}`}
                onClick={() => setActiveTab('github')}
                title="GitHub"
                aria-label="GitHub"
              >
                <Github size={16} />
                <span>GitHub</span>
              </button>
            </div>
          )}

          <div className={styles.tabPanel}>
        {effectiveTab === 'attachments' && (
          <>
            <div className={`${styles.content} ${styles.thinScroll}`}>
              {isLoading ? (
                <div className={styles.loading}>
                  <Loader2 className={styles.spinning} />
                  <span>Loading attachments...</span>
                </div>
              ) : !data?.attachments?.length ? (
                <div className={styles.empty}>
                  <FolderOpen className={styles.emptyIcon} />
                  <p>No attachments yet</p>
                  <p className={styles.emptyHint}>
                    Upload files or drag images into chat to build your library
                  </p>
                </div>
              ) : !filteredAttachments.length ? (
                <div className={styles.empty}>
                  <File className={styles.emptyIcon} />
                  <p>No matching attachments</p>
                  <p className={styles.emptyHint}>
                    Try a different search or asset type filter
                  </p>
                </div>
              ) : (
                <div style={getGridStyle()}>
                  {filteredAttachments.map((attachment) => {
                    const isImage = attachment.fileType?.startsWith('image/');
                    const isPdf = attachment.fileType === 'application/pdf' || attachment.fileName?.toLowerCase().endsWith('.pdf');
                    const isGlb = isGlbFile(attachment.fileName || '') || isGlbFile(attachment.fileType || '');
                    const preview = isImage ? (
                      <img src={attachment.dataUrl} alt={attachment.fileName} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    ) : isPdf ? (
                      <img src="https://tasking.tech/assets/taskingbot-pdf-cover.png" alt="PDF document" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : isGlb ? (
                      <GlbViewer src={attachment.dataUrl} name={attachment.fileName} compact />
                    ) : (
                      getFileIcon(attachment.fileType)
                    );

                    const actions = (
                      <>
                        {(isImage || isGlb) && (
                          <Tooltip><TooltipTrigger asChild>
                            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setPreviewAttachment(attachment); }}>
                              <Eye size={14} />
                            </Button>
                          </TooltipTrigger><TooltipContent>Preview</TooltipContent></Tooltip>
                        )}
                        {isImage && onImageEnhance && (
                          <Tooltip><TooltipTrigger asChild>
                            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); onImageEnhance(attachment.dataUrl); }}>
                              <WandSparkles size={14} />
                            </Button>
                          </TooltipTrigger><TooltipContent>Enhance</TooltipContent></Tooltip>
                        )}
                        {isImage && onRemoveBackground && (
                          <Tooltip><TooltipTrigger asChild>
                            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); onRemoveBackground(attachment.dataUrl); }}>
                              <Eraser size={14} />
                            </Button>
                          </TooltipTrigger><TooltipContent>Remove Background</TooltipContent></Tooltip>
                        )}
                        {onSendToChat && (
                          <Tooltip><TooltipTrigger asChild>
                            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); onSendToChat(attachment); }}>
                              <SendHorizontal size={14} />
                            </Button>
                          </TooltipTrigger><TooltipContent>Send to Chat</TooltipContent></Tooltip>
                        )}
                        <Tooltip><TooltipTrigger asChild>
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleDownload(attachment); }}>
                            <Download size={14} />
                          </Button>
                        </TooltipTrigger><TooltipContent>Download</TooltipContent></Tooltip>
                        <Tooltip><TooltipTrigger asChild>
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setShowShareModal(attachment.id); }}
                            style={{ color: attachment.isPrivate ? undefined : 'var(--primary)' }}>
                            <Link2 size={14} />
                          </Button>
                        </TooltipTrigger><TooltipContent>Share</TooltipContent></Tooltip>
                        <Tooltip><TooltipTrigger asChild>
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleDelete(attachment.id); }} disabled={deleteMutation.isPending}
                            style={{ color: '#ef4444' }}>
                            <Trash2 size={14} />
                          </Button>
                        </TooltipTrigger><TooltipContent>Delete</TooltipContent></Tooltip>
                      </>
                    );

                    return renderAssetItem(
                      attachment.id,
                      preview,
                      attachment.fileName,
                      attachment.fileType,
                      attachment.fileSize,
                      attachment.createdAt,
                      null,
                      null,
                      actions,
                      () => {
                        if (isImage || isPdf || isGlb) setPreviewAttachment(attachment);
                        else onSelectAttachment?.(attachment);
                      },
                      (e) => {
                        e.dataTransfer.setData('text/plain', attachment.dataUrl);
                        e.dataTransfer.setData('application/x-attachment-id', attachment.id);
                      },
                      attachment.id,
                      'attachment',
                      attachment.dataUrl,
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}

        {/* Generated Media Tab */}
        {effectiveTab === 'generated' && (
          <>
            {/* Folder navigation */}
            <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
              {/* Breadcrumbs */}
              <button
                onClick={() => navigateToBreadcrumb(-1)}
                onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.background = 'color-mix(in srgb, var(--primary) 20%, transparent)'; }}
                onDragLeave={(e) => { e.currentTarget.style.background = currentFolderId ? 'transparent' : 'color-mix(in srgb, var(--primary) 12%, transparent)'; }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.style.background = currentFolderId ? 'transparent' : 'color-mix(in srgb, var(--primary) 12%, transparent)';
                  const draggedFolderId = e.dataTransfer.getData('application/x-folder-id');
                  if (draggedFolderId) { handleMoveFolderToFolder(draggedFolderId, null); return; }
                  const assetId = e.dataTransfer.getData('application/x-media-id');
                  if (assetId) handleMoveAssetToFolder(assetId, null);
                }}
                style={{
                  padding: '3px 8px', borderRadius: '6px', border: 'none', cursor: 'pointer',
                  background: currentFolderId ? 'transparent' : 'color-mix(in srgb, var(--primary) 12%, transparent)',
                  color: currentFolderId ? 'var(--muted-foreground)' : 'var(--primary)',
                  fontSize: '0.72rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px',
                }}
              >
                <FolderOpen size={12} /> All Files
              </button>
              {folderBreadcrumbs.map((crumb, i) => (
                <React.Fragment key={crumb.id}>
                  <ChevronRight size={10} style={{ color: 'var(--muted-foreground)', opacity: 0.5, flexShrink: 0 }} />
                  <button
                    onClick={() => navigateToBreadcrumb(i)}
                    onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.background = 'color-mix(in srgb, var(--primary) 20%, transparent)'; }}
                    onDragLeave={(e) => { e.currentTarget.style.background = i === folderBreadcrumbs.length - 1 ? 'color-mix(in srgb, var(--primary) 12%, transparent)' : 'transparent'; }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.currentTarget.style.background = i === folderBreadcrumbs.length - 1 ? 'color-mix(in srgb, var(--primary) 12%, transparent)' : 'transparent';
                      const draggedFolderId = e.dataTransfer.getData('application/x-folder-id');
                      if (draggedFolderId && draggedFolderId !== crumb.id) { handleMoveFolderToFolder(draggedFolderId, crumb.id); return; }
                      const assetId = e.dataTransfer.getData('application/x-media-id');
                      if (assetId) handleMoveAssetToFolder(assetId, crumb.id);
                    }}
                    style={{
                      padding: '3px 8px', borderRadius: '6px', border: 'none', cursor: 'pointer',
                      background: i === folderBreadcrumbs.length - 1 ? 'color-mix(in srgb, var(--primary) 12%, transparent)' : 'transparent',
                      color: i === folderBreadcrumbs.length - 1 ? 'var(--primary)' : 'var(--muted-foreground)',
                      fontSize: '0.72rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px',
                    }}
                  >
                    <Folder size={11} /> {crumb.name}
                  </button>
                </React.Fragment>
              ))}

              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '4px' }}>
                {showCreateFolder ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <input
                      autoFocus
                      placeholder="Folder name"
                      value={newFolderName}
                      onChange={(e) => setNewFolderName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleCreateFolder(); if (e.key === 'Escape') { setShowCreateFolder(false); setNewFolderName(''); } }}
                      style={{
                        padding: '4px 8px', borderRadius: '6px', border: '1px solid var(--border)',
                        background: 'var(--background)', color: 'var(--foreground)', fontSize: '0.72rem', width: '120px',
                      }}
                    />
                    <button onClick={handleCreateFolder} style={{ padding: '4px', border: 'none', background: 'var(--primary)', color: '#fff', borderRadius: '4px', cursor: 'pointer', display: 'flex' }}>
                      <Check size={12} />
                    </button>
                    <button onClick={() => { setShowCreateFolder(false); setNewFolderName(''); }} style={{ padding: '4px', border: 'none', background: 'transparent', color: 'var(--muted-foreground)', cursor: 'pointer', display: 'flex' }}>
                      <X size={12} />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowCreateFolder(true)}
                    title="New folder"
                    style={{
                      padding: '4px 8px', borderRadius: '6px', border: '1px dashed var(--border)',
                      background: 'transparent', color: 'var(--muted-foreground)', cursor: 'pointer',
                      fontSize: '0.68rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px',
                    }}
                  >
                    <FolderPlus size={12} /> New Folder
                  </button>
                )}
              </div>
            </div>

            <div className={`${styles.content} ${styles.thinScroll}`}>
              {isLoadingMedia ? (
                <div className={styles.loading}>
                  <Loader2 className={styles.spinning} />
                  <span>Loading generated media...</span>
                </div>
              ) : !filteredMediaItems.length && !foldersData?.folders?.length ? (
                <div className={styles.empty}>
                  <Sparkles className={styles.emptyIcon} />
                  <p>{currentFolderId ? 'This folder is empty' : 'No assets yet'}</p>
                  <p className={styles.emptyHint}>
                    {currentFolderId ? 'Drag files into this folder or upload new ones' : 'Upload files or ask TaskingBot to generate images, audio, video, or documents'}
                  </p>
                </div>
              ) : !filteredMediaItems.length && foldersData?.folders?.length ? (
                null
              ) : !filteredMediaItems.length ? (
                <div className={styles.empty}>
                  <Sparkles className={styles.emptyIcon} />
                  <p>No matching generated assets</p>
                  <p className={styles.emptyHint}>
                    Try a different search or asset type filter
                  </p>
                </div>
              ) : (
                <div style={getGridStyle()}>
                  {filteredMediaItems.map((item) => {
                    const lowerName = (item.fileName || '').toLowerCase();
                    const isImage = item.fileType?.startsWith('image/') || /\.(png|jpg|jpeg|gif|webp|svg|ico|bmp|avif)$/.test(lowerName);
                    const isPdf = item.fileType === 'application/pdf' || lowerName.endsWith('.pdf') || item.url?.toLowerCase().endsWith('.pdf');
                    const toPreviewAttachment = () => ({
                      id: item.id, fileName: item.fileName, fileType: isPdf ? 'application/pdf' : item.fileType, fileSize: item.fileSize,
                      dataUrl: item.url, isPrivate: true, shareToken: null, sharedWith: null,
                      createdAt: item.createdAt, sessionId: '',
                      uploadedBy: { id: item.userId, displayName: null, avatarUrl: null },
                    } as any);

                    const preview = isImage ? (
                      <img src={item.thumbnailUrl || item.url} alt={item.fileName} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    ) : isPdf ? (
                      <img src="https://tasking.tech/assets/taskingbot-pdf-cover.png" alt="PDF document" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      getMediaIcon(item)
                    );

                    const actions = (
                      <>
                        {(isImage || isPdf) && (
                          <Tooltip><TooltipTrigger asChild>
                            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setPreviewAttachment(toPreviewAttachment()); }}>
                              <Eye size={14} />
                            </Button>
                          </TooltipTrigger><TooltipContent>{isPdf ? 'View PDF' : 'Preview'}</TooltipContent></Tooltip>
                        )}
                        {isImage && onImageEnhance && (
                          <Tooltip><TooltipTrigger asChild>
                            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); onImageEnhance(item.url); }}>
                              <WandSparkles size={14} />
                            </Button>
                          </TooltipTrigger><TooltipContent>Enhance</TooltipContent></Tooltip>
                        )}
                        {isImage && onRemoveBackground && (
                          <Tooltip><TooltipTrigger asChild>
                            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); onRemoveBackground(item.url); }}>
                              <Eraser size={14} />
                            </Button>
                          </TooltipTrigger><TooltipContent>Remove Background</TooltipContent></Tooltip>
                        )}
                        {onSendToChat && (
                          <Tooltip><TooltipTrigger asChild>
                            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); onSendToChat(toPreviewAttachment()); }}>
                              <SendHorizontal size={14} />
                            </Button>
                          </TooltipTrigger><TooltipContent>Send to Chat</TooltipContent></Tooltip>
                        )}
                        {isPdf && (
                          <Tooltip><TooltipTrigger asChild>
                            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setPdfEditorUrl(item.url); setShowPdfEditor(true); }}>
                              <FileText size={14} />
                            </Button>
                          </TooltipTrigger><TooltipContent>Edit PDF</TooltipContent></Tooltip>
                        )}
                        <Tooltip><TooltipTrigger asChild>
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleDownloadMedia(item); }}>
                            <Download size={14} />
                          </Button>
                        </TooltipTrigger><TooltipContent>Download</TooltipContent></Tooltip>
                        <Tooltip><TooltipTrigger asChild>
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setShowShareModal(item.id); }}>
                            <Link2 size={14} />
                          </Button>
                        </TooltipTrigger><TooltipContent>Share</TooltipContent></Tooltip>
                        <Tooltip><TooltipTrigger asChild>
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleDeleteMedia(item.id); }} disabled={deleteMediaMutation.isPending}
                            style={{ color: '#ef4444' }}>
                            <Trash2 size={14} />
                          </Button>
                        </TooltipTrigger><TooltipContent>Delete</TooltipContent></Tooltip>
                      </>
                    );

                    return renderAssetItem(
                      item.id,
                      preview,
                      item.fileName,
                      item.fileType,
                      item.fileSize,
                      item.createdAt,
                      item.provider,
                      item.prompt,
                      actions,
                      () => {
                        if (isImage || isPdf) setPreviewAttachment(toPreviewAttachment());
                      },
                      (e) => {
                        e.dataTransfer.setData('application/x-media-id', item.id);
                        e.dataTransfer.setData('text/plain', item.url);
                      },
                      item.id,
                      'media',
                      item.url,
                    );
                  })}
                </div>
              )}
              {hasMoreMedia && !mediaError && (
                <div
                  ref={(el) => {
                    if (!el) return;
                    const observer = new IntersectionObserver(
                      (entries) => {
                        if (entries[0]?.isIntersecting && !isLoadingMedia && hasMoreMedia) {
                          loadMoreMedia();
                        }
                      },
                      { rootMargin: '200px' },
                    );
                    observer.observe(el);
                    return () => observer.disconnect();
                  }}
                  style={{ height: 1 }}
                />
              )}
              {isLoadingMedia && allMediaItems.length > 0 && (
                <div style={{ textAlign: 'center', padding: '8px 0' }}>
                  <div style={{ width: 16, height: 16, margin: '0 auto', border: '2px solid var(--border)', borderTopColor: 'var(--primary)', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                </div>
              )}
            </div>
          </>
        )}

        {/* GitHub Tab Content */}
        {effectiveTab === 'github' && (
          <div className={styles.content}>
            {!hasGithubIntegration ? (
              <div className={styles.empty}>
                <Github className={styles.emptyIcon} />
                <p>Connect GitHub to browse your repositories</p>
                <Button onClick={connectGitHub} className={styles.connectButton}>
                  <Github size={16} />
                  Connect GitHub
                </Button>
              </div>
            ) : !selectedRepo ? (
              <>
                <div className={styles.toolbar}>
                  <Input
                    placeholder="Search repositories..."
                    value={repoSearch}
                    onChange={(e) => setRepoSearch(e.target.value)}
                    className={styles.searchInput}
                  />
                </div>
                <div className={styles.repoList}>
                  {isLoadingRepos ? (
                    <div className={styles.loading}>
                      <Loader2 className={styles.spinning} />
                      <span>Loading repositories...</span>
                    </div>
                  ) : !filteredRepos?.length ? (
                    <div className={styles.empty}>
                      <p>No repositories found</p>
                    </div>
                  ) : (
                    filteredRepos.map((repo) => (
                      <button
                        key={repo.id}
                        className={styles.repoItem}
                        onClick={() => handleSelectRepo(repo.full_name, repo.default_branch)}
                      >
                        <Folder className={styles.repoIcon} />
                        <span className={styles.repoName}>{repo.full_name}</span>
                        <ChevronRight size={16} className={styles.chevron} />
                      </button>
                    ))
                  )}
                </div>
              </>
            ) : (
              <>
                <div className={styles.toolbar}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleGoBack}
                    title="Go back"
                    aria-label="Go back"
                  >
                    <ArrowLeft size={14} />
                    Back
                  </Button>
                  <div className={styles.breadcrumb}>
                    <span className={styles.repoLabel}>{selectedRepo}</span>
                    {currentPath && (
                      <>
                        <ChevronRight size={12} />
                        <span>{currentPath}</span>
                      </>
                    )}
                  </div>
                </div>
                <div className={styles.fileList}>
                  {isLoadingContents ? (
                    <div className={styles.loading}>
                      <Loader2 className={styles.spinning} />
                      <span>Loading contents...</span>
                    </div>
                  ) : !contentsData?.contents?.length ? (
                    <div className={styles.empty}>
                      <p>This folder is empty</p>
                    </div>
                  ) : (
                    contentsData.contents
                      .sort((a, b) => {
                        // Directories first, then files
                        if (a.type === 'dir' && b.type !== 'dir') return -1;
                        if (a.type !== 'dir' && b.type === 'dir') return 1;
                        return a.name.localeCompare(b.name);
                      })
                      .map((item) => (
                        <div
                          key={item.path}
                          className={styles.fileItem}
                          onClick={() => item.type === 'dir' && handleNavigate(item)}
                        >
                          {getGitHubIcon(item.type, item.name)}
                          <span className={styles.fileName}>{item.name}</span>
                          {item.type === 'dir' ? (
                            <ChevronRight size={16} className={styles.chevron} />
                          ) : (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleImportFile(item.path, item.name);
                                  }}
                                  disabled={importingFiles.has(item.path)}
                                >
                                  {importingFiles.has(item.path) ? (
                                    <Loader2 size={14} className={styles.spinning} />
                                  ) : (
                                    <Plus size={14} />
                                  )}
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Add to library</TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                      ))
                  )}
                </div>
              </>
            )}
          </div>
        )}
          </div>
        </div>
      </div>
      </div>{/* close content wrapper from sidebar layout */}

      {lightbox}
      {showPdfEditor && pdfEditorUrl && (() => {
        const PdfEditorLazy = React.lazy(() => import('./PdfEditor').then(m => ({ default: m.PdfEditor })));
        return (
          <React.Suspense fallback={null}>
            <PdfEditorLazy
              pdfUrl={pdfEditorUrl}
              onClose={() => { setShowPdfEditor(false); setPdfEditorUrl(''); }}
              onSaved={(newUrl: string) => {
                if (previewAttachment) {
                  setPreviewAttachment({ ...previewAttachment, dataUrl: newUrl });
                }
              }}
            />
          </React.Suspense>
        );
      })()}

      {/* Share dialog (Google Drive / Dropbox style) */}
      {showShareModal && (() => {
        const isFolder = showShareModal.startsWith('folder:');
        const realId = isFolder ? showShareModal.replace('folder:', '') : showShareModal;
        const att = !isFolder ? data?.attachments?.find(a => a.id === realId) : null;
        const med = !isFolder ? allMediaItems?.find(m => m.id === realId) : null;
        const folder = isFolder ? foldersData?.folders?.find((f: any) => f.id === realId) : null;
        const assetName = att?.fileName || med?.fileName || folder?.name || 'Asset';
        const assetType: 'attachment' | 'media' | 'folder' = isFolder ? 'folder' : att ? 'attachment' : 'media';
        return (
          <ShareAssetDialog
            open={!!showShareModal}
            onOpenChange={(open) => { if (!open) setShowShareModal(null); }}
            assetId={realId}
            assetType={assetType}
            assetName={assetName}
          />
        );
      })()}
    </>
  );

  if (variant === 'inline') {
    return <div className={styles.inlineShell}>{panel}</div>;
  }

  return (
    <div className={styles.overlay} onClick={onClose}>
      {panel}
    </div>
  );
};

export default ChatAttachmentsLibrary;
